// Stage configuration for the pipeline stage modals.
// Each stage only declares its fields; StageModal renders, validates and persists them.

import { MEDICAL_AID_OPTIONS } from './constants';

export const FIELD_TYPES = {
  TEXT: 'text',
  DATE: 'date',
  SELECT: 'select',
  MULTI_SELECT: 'multi_select',
  TEXTAREA: 'textarea',
  AUTO_CALC: 'auto_calc',
  CHECKBOX: 'checkbox',
  NUMBER: 'number',
};

export const createStageConfig = (stageName, fields) => ({
  stageName,
  dataFieldName: `${stageName
    .toLowerCase()
    .replace(/\//g, '_')
    .replace(/\s+/g, '_')}_data`,
  fields,
});

export const createField = (config) => ({
  id: config.id,
  label: config.label,
  type: config.type,
  section: config.section || null,
  placeholder: config.placeholder || '',
  options: config.options || [],
  // Options that depend on another answer: { fieldId, byValue: { answer: [...] } }
  dynamicOptions: config.dynamicOptions || null,
  // Answers to clear when this field changes (e.g. funding method drives authorisation/payment)
  resetOnChange: config.resetOnChange || null,
  required: config.required || false,
  requiredIf: config.requiredIf || null, // { fieldId, value } or { fieldId, greaterThan }
  min: config.min ?? null,
  max: config.max ?? null,
  hidden: config.hidden || false,
  visibleIf: config.visibleIf || null,
  autoCalcFrom: config.autoCalcFrom || null,
  triggersLostCancelled: config.triggersLostCancelled || false,
  // Starts on today's date when the field is still empty
  autoFillToday: config.autoFillToday || false,
  // Locks on its own once saved, even if the rest of its section is still editable
  readOnlyAfterSave: config.readOnlyAfterSave || false,
  // Lost/Cancelled is only offered after the stage was saved, not the moment the reason is picked
  confirmAfterSave: config.confirmAfterSave || false,
  // After saving, offer to send the case back to an earlier stage (e.g. for rescheduling)
  offersSendBackTo: config.offersSendBackTo || null,
  helpText: config.helpText || '',
});

// ---------- Shared option lists ----------
const YES_NO = ['Yes', 'No'];

const DELAY_REASONS = [
  'No answer',
  'Gather funds',
  'Patient sick',
  'Wrong contact info',
  'Phone back later',
  'Work responsibilities',
  'Personal commitments',
  'Other health priorities',
  'Family discussion',
];

const CANCELLATION_REASONS = [
  'Funds',
  'Not interested',
  'Already did study',
  'Insomnia referral',
  'Patient already on CPAP',
  'Patient referred for TT',
  'Level 1 PSG needed',
  'Pulmonology referral',
  'Sleep problem resolved',
  'Other health priorities',
  'Unaware of reason for referral',
];

const SCHEDULERS = ['FB', 'FRA', 'MNO', 'DCO', 'KIR', 'CRW'];

const PLACES_OF_FACILITATION = [
  'PTA office',
  'WTB office',
  'Home visit',
  'In-hospital',
  'Telephonic assistance',
];

// ============ STAGE 1: REFERRAL ============
export const REFERRAL_CONFIG = createStageConfig('Referral', [
  createField({
    id: 'lead_generator',
    label: 'Lead Generator',
    type: FIELD_TYPES.SELECT,
    section: 'Referral',
    options: ['Website', 'Intercare', 'Ecomed', 'Facebook', 'Medical reps'],
    required: true,
  }),
  createField({
    id: 'referral_date',
    label: 'Date Referral Received',
    type: FIELD_TYPES.DATE,
    section: 'Referral',
    required: true,
  }),
  createField({
    id: 'patient_name',
    label: 'Patient Name',
    type: FIELD_TYPES.TEXT,
    section: 'Patient',
    placeholder: 'From GoodX',
    required: true,
  }),
  createField({
    id: 'patient_surname',
    label: 'Patient Surname',
    type: FIELD_TYPES.TEXT,
    section: 'Patient',
    placeholder: 'From GoodX',
    required: true,
  }),
  createField({
    id: 'tin_number',
    label: 'TIN Number',
    type: FIELD_TYPES.TEXT,
    section: 'Patient',
    placeholder: 'From GoodX',
    required: true,
  }),
  createField({
    id: 'medical_aid_name',
    label: 'Medical Aid',
    type: FIELD_TYPES.SELECT,
    section: 'Medical Aid',
    required: true,
    helpText: 'Options are supplied by the page from /api/medical-aids',
  }),
  createField({
    id: 'medical_aid_plan',
    label: 'Medical Aid Plan',
    type: FIELD_TYPES.SELECT,
    section: 'Medical Aid',
    required: true,
  }),
  createField({
    id: 'doctor_id',
    label: 'Doctor That Referred Patient',
    type: FIELD_TYPES.SELECT,
    section: 'Referring Doctor',
    required: true,
  }),
  createField({
    id: 'referral_from_dr_rooms',
    label: "Referral Sent from Dr's Rooms?",
    type: FIELD_TYPES.SELECT,
    section: 'Referring Doctor',
    options: YES_NO,
    required: true,
  }),
  createField({
    id: 'out_of_hospital',
    label: 'Out of Hospital?',
    type: FIELD_TYPES.SELECT,
    section: 'Referring Doctor',
    options: YES_NO,
    required: true,
  }),
]);

// ============ STAGE 2: PRE SS ============
const contactAttemptFields = (n) => {
  const section = `Contact ${n}`;
  return [
    createField({
      id: `contact_${n}_date`,
      label: `Contact ${n} Date`,
      type: FIELD_TYPES.DATE,
      section,
    }),
    createField({
      id: `contact_${n}_lead_time`,
      label: 'Lead Time (auto)',
      type: FIELD_TYPES.AUTO_CALC,
      section,
      autoCalcFrom: {
        startField: n === 1 ? 'referral_date' : `contact_${n - 1}_date`,
        endField: `contact_${n}_date`,
        format: 'days',
      },
      helpText: n === 1 ? 'Auto-calculated from referral date' : `Auto-calculated from contact ${n - 1}`,
    }),
    createField({
      id: `contact_${n}_delay_reason`,
      label: 'Reason for Delay',
      type: FIELD_TYPES.SELECT,
      section,
      options: DELAY_REASONS,
    }),
    createField({
      id: `contact_${n}_cancellation_reason`,
      label: 'Cancellation Reason',
      type: FIELD_TYPES.SELECT,
      section,
      options: CANCELLATION_REASONS,
      triggersLostCancelled: true,
    }),
  ];
};

export const PRE_SS_CONFIG = createStageConfig('Pre SS', [
  ...contactAttemptFields(1),
  ...contactAttemptFields(2),
  ...contactAttemptFields(3),
  ...contactAttemptFields(4),
  ...contactAttemptFields(5),

  // Sleep Study Schedule
  createField({
    id: 'scheduler',
    label: 'Scheduler',
    type: FIELD_TYPES.SELECT,
    section: 'Sleep Study Schedule',
    options: SCHEDULERS,
  }),
  createField({
    id: 'today_date',
    label: "Today's Date",
    type: FIELD_TYPES.DATE,
    section: 'Sleep Study Schedule',
    helpText: "Date the study was scheduled on",
  }),
  createField({
    id: 'scheduled_sleep_study_date',
    label: 'Scheduled Sleep Study Date',
    type: FIELD_TYPES.DATE,
    section: 'Sleep Study Schedule',
    required: true,
  }),
  createField({
    id: 'scheduled_lead_time',
    label: 'Lead Time (auto)',
    type: FIELD_TYPES.AUTO_CALC,
    section: 'Sleep Study Schedule',
    autoCalcFrom: { startField: 'today_date', endField: 'scheduled_sleep_study_date', format: 'days' },
  }),
  createField({
    id: 'schedule_delay_reason',
    label: 'Delay Reason',
    type: FIELD_TYPES.SELECT,
    section: 'Sleep Study Schedule',
    options: [
      'Gather funds',
      'Patient sick',
      'Work commitment',
      'Personal commitments',
      'Equipment availability',
      'Other health priorities',
      'Patient requested date',
      'Courier 2 days',
    ],
    requiredIf: { fieldId: 'scheduled_lead_time', greaterThan: 4 },
    helpText: 'Required when the lead time is more than 4 days',
  }),
  createField({
    id: 'place_of_facilitation',
    label: 'Place of Facilitation',
    type: FIELD_TYPES.SELECT,
    section: 'Sleep Study Schedule',
    options: PLACES_OF_FACILITATION,
  }),

  // Reschedule
  createField({
    id: 'reschedule_scheduler',
    label: 'Reschedule Scheduler',
    type: FIELD_TYPES.SELECT,
    section: 'Reschedule',
    options: SCHEDULERS,
  }),
  createField({
    id: 'previous_psg_date',
    label: 'Previous PSG Date',
    type: FIELD_TYPES.DATE,
    section: 'Reschedule',
  }),
  createField({
    id: 'new_scheduled_study_date',
    label: 'New Scheduled Sleep Study Date',
    type: FIELD_TYPES.DATE,
    section: 'Reschedule',
  }),
  createField({
    id: 'reschedule_lead_time',
    label: 'Reschedule Lead Time (auto)',
    type: FIELD_TYPES.AUTO_CALC,
    section: 'Reschedule',
    autoCalcFrom: { startField: 'previous_psg_date', endField: 'new_scheduled_study_date', format: 'days' },
  }),
  createField({
    id: 'reschedule_delay_reason',
    label: 'Reschedule Delay Reason',
    type: FIELD_TYPES.SELECT,
    section: 'Reschedule',
    options: [
      'Gather funds',
      'Patient sick',
      'Work commitment',
      'Personal commitments',
      'No pitch for appointment',
      'Chronic registration',
    ],
  }),
  createField({
    id: 'reschedule_place_of_facilitation',
    label: 'Reschedule Place of Facilitation',
    type: FIELD_TYPES.SELECT,
    section: 'Reschedule',
    options: PLACES_OF_FACILITATION,
  }),

  // Appointment Confirmation
  createField({
    id: 'confirmation_date',
    label: 'Date of Confirmation',
    type: FIELD_TYPES.DATE,
    section: 'Appointment Confirmation',
  }),
  createField({
    id: 'appointment_confirmed',
    label: 'Appointment Confirmed',
    type: FIELD_TYPES.SELECT,
    section: 'Appointment Confirmation',
    options: YES_NO,
  }),
  createField({
    id: 'cancellation_reason_confirmation',
    label: 'Cancellation Reason',
    type: FIELD_TYPES.SELECT,
    section: 'Appointment Confirmation',
    options: ['Funds', 'Not interested', 'Reschedule'],
    visibleIf: { fieldId: 'appointment_confirmed', value: 'No' },
    requiredIf: { fieldId: 'appointment_confirmed', value: 'No' },
    triggersLostCancelled: true,
  }),
  createField({
    id: 'payment_method',
    label: 'Payment Method',
    type: FIELD_TYPES.SELECT,
    section: 'Appointment Confirmation',
    options: [
      'Authorization granted',
      'Medical savings',
      'Private',
      'Other benefits',
      'Auth + co-payment',
      'Savings + co-payment',
    ],
    visibleIf: { fieldId: 'appointment_confirmed', value: 'Yes' },
    requiredIf: { fieldId: 'appointment_confirmed', value: 'Yes' },
  }),
]);

// ============ STAGE 3: SS FACILITATION ============

// ---------- Stubs for the remaining stages ----------
const stubStage = (stageName, dateFieldId, dateLabel) =>
  createStageConfig(stageName, [
    createField({
      id: dateFieldId,
      label: dateLabel,
      type: FIELD_TYPES.DATE,
      section: stageName,
      required: true,
    }),
    createField({
      id: 'responsible_person',
      label: 'Responsible Person',
      type: FIELD_TYPES.TEXT,
      section: stageName,
      placeholder: 'Who is handling this step',
    }),
    createField({
      id: 'cancellation_reason',
      label: 'Cancellation Reason',
      type: FIELD_TYPES.SELECT,
      section: 'Outcome',
      options: CANCELLATION_REASONS,
      triggersLostCancelled: true,
    }),
    createField({
      id: 'notes',
      label: 'Notes',
      type: FIELD_TYPES.TEXTAREA,
      section: 'Outcome',
      placeholder: 'Notes for this stage',
    }),
  ]);

// ============ STAGE 4: SS AUTHORIZATION ============
export const SS_AUTHORIZATION_CONFIG = createStageConfig('SS Authorization', [
  createField({
    id: 'auth_date',
    label: 'Authorization Date',
    type: FIELD_TYPES.DATE,
    section: 'SS Authorization',
    required: true,
  }),
  createField({
    id: 'auth_number',
    label: 'Authorization Number',
    type: FIELD_TYPES.TEXT,
    section: 'SS Authorization',
    required: true,
  }),
  createField({
    id: 'auth_outcome',
    label: 'Authorization Outcome',
    type: FIELD_TYPES.SELECT,
    section: 'SS Authorization',
    options: ['Approved', 'Declined', 'Pending', 'Self-funded'],
  }),
  createField({
    id: 'cancellation_reason',
    label: 'Cancellation Reason',
    type: FIELD_TYPES.SELECT,
    section: 'Outcome',
    options: CANCELLATION_REASONS,
    triggersLostCancelled: true,
  }),
  createField({
    id: 'notes',
    label: 'Notes',
    type: FIELD_TYPES.TEXTAREA,
    section: 'Outcome',
  }),
]);

// ============ STAGE 5: SS SUCCESS ============
export const SS_SUCCESS_CONFIG = stubStage('SS Success', 'ss_success_date', 'Sleep Study Success Date');

// ============ STAGE 6: POST SS ============
export const POST_SS_CONFIG = stubStage('Post SS', 'post_ss_date', 'Post SS Date');

// ============ STAGE 7: SS REPORTS ============

// ============ STAGE 8: TT FACILITATION ============

// ============ STAGE 9: POST TT ============
export const POST_TT_CONFIG = stubStage('Post TT', 'post_tt_date', 'Post TT Date');

// ============ STAGE 10: TT SUM ============
export const TT_SUM_CONFIG = stubStage('TT Sum', 'tt_sum_date', 'TT Summary Date');

// ============ STAGE 11: TT REPORT ============

// ============ SPECIAL STAGE: LOST/CANCELLED ============
export const LOST_CANCELLED_CONFIG = createStageConfig('Lost/Cancelled', [
  createField({
    id: 'cancellation_stage',
    label: 'Stage Lost/Cancelled From',
    type: FIELD_TYPES.TEXT,
    section: 'Lost/Cancelled',
    helpText: 'Auto-filled with the stage the patient was lost from',
  }),
  createField({
    id: 'cancellation_date',
    label: 'Date Lost/Cancelled',
    type: FIELD_TYPES.DATE,
    section: 'Lost/Cancelled',
    required: true,
  }),
  createField({
    id: 'cancellation_reason_final',
    label: 'Cancellation Reason',
    type: FIELD_TYPES.SELECT,
    section: 'Lost/Cancelled',
    options: CANCELLATION_REASONS,
    required: true,
  }),
  createField({
    id: 're_engage',
    label: 'Re-engage Later?',
    type: FIELD_TYPES.SELECT,
    section: 'Re-engagement',
    options: YES_NO,
  }),
  createField({
    id: 're_engage_date',
    label: 'Re-engage On',
    type: FIELD_TYPES.DATE,
    section: 'Re-engagement',
    visibleIf: { fieldId: 're_engage', value: 'Yes' },
    requiredIf: { fieldId: 're_engage', value: 'Yes' },
  }),
  createField({
    id: 'cancellation_notes',
    label: 'Additional Notes',
    type: FIELD_TYPES.TEXTAREA,
    section: 'Re-engagement',
    placeholder: 'Any additional context about why this case was lost',
  }),
]);

// ---------- Helpers for the dual-pipeline stage forms ----------
// Some backend fields use condensed names (ss_followup_data, tt_nextday_fu_data),
// so those two configs override the generated data field name.
const withDataField = (config, dataFieldName) => ({ ...config, dataFieldName });

const RESPONSIBLE_PEOPLE = ['KIR', 'FRA', 'DCO', 'MNO', 'CRW'];
const FACILITATORS_SS = ['DCO', 'FRA', 'MNO'];
const FACILITATORS_TT = ['DCO', 'FRA', 'MNO', 'KIR'];
const APPOINTMENT_OUTCOMES = ['Yes', 'No', 'Cancel'];
const PLACES = ['PTA office', 'WTB Office', 'Home Visit', 'In-Hospital', 'Virtual Facilitation'];
const AUTH_TYPES = [
  'Authorisation Granted',
  'Medical Savings',
  'Other Benefits',
  'Auth + Co-payment',
  'Savings + Co-Payment',
];
// How a medical aid case actually gets paid, plus the private fallback.
const MEDICAL_AID_PAYMENT_METHODS = [
  'Authorization + Co-payment',
  'Authorization Only',
  'Co-payment Only',
  'Medical Aid Declined',
];
const FACILITATION_DELAY_REASONS = [
  'Patient Delay',
  'Courier Delay',
  'Patient sick',
  'Power outage',
  'No answer',
];
const FACILITATION_CANCELLATION_REASONS = [
  'Pt postponed',
  'Not interested',
  'Finances',
  "Didn't pitch",
  'No answer',
  'Pt ill',
  'Work responsibility',
];
const TT_REDO_REASONS = [
  'High AHI',
  'High pressures',
  'High Central Apnea',
  'Limited hours',
  'Pt requested',
  'ASV',
  'No data',
  'Pressure variation',
  'GRT Requested',
  '2 week trial',
];
const FEEDBACK_NOT_PROVIDED_REASONS = [
  'No answer',
  'Patient busy',
  'Patient requested to call back at another time',
];

// ============ SS STAGE 2: SS SCHEDULE ============
const SS_CONTACT_DELAY_REASONS = [
  'No answer',
  'Gather Funds',
  'Patient Sick',
  'Wrong contact Info',
  'Phone back later',
  'Work responsibilities',
  'Personal commitments',
  'Other Health Priorities',
  'Family Discussion',
];

const SS_CONTACT_CANCELLATION_REASONS = [
  'Funds',
  'Not Interested',
  'Already did study',
  'Insomnia Referral',
  'Patient already on CPAP',
  'Patient referred for TT',
  'Level 1 PSG needed',
  'Pulmonology Referral',
  'Sleep Problem Resolved',
  'Other Health Priorities',
  'Unaware of reason for Referral',
];

const ssContactAttempt = (contactNum) => {
  const previous = contactNum === 1 ? 'referral_date' : `contact_${contactNum - 1}_date`;
  const section = `Contact ${contactNum}`;
  return [
    createField({
      id: `contact_${contactNum}_date`,
      label: 'Date',
      type: FIELD_TYPES.DATE,
      section,
    }),
    createField({
      id: `contact_${contactNum}_lead_time`,
      label: 'Lead Time (auto)',
      type: FIELD_TYPES.AUTO_CALC,
      section,
      autoCalcFrom: { startField: previous, endField: `contact_${contactNum}_date`, format: 'days' },
      helpText: contactNum === 1 ? 'From referral date' : `From contact ${contactNum - 1} date`,
    }),
    createField({
      id: `contact_${contactNum}_appointment_made`,
      label: `Appointment Made?`,
      type: FIELD_TYPES.SELECT,
      section,
      options: APPOINTMENT_OUTCOMES,
    }),
    createField({
      id: `contact_${contactNum}_delay_reason`,
      label: `Delay Reason`,
      type: FIELD_TYPES.SELECT,
      section,
      options: SS_CONTACT_DELAY_REASONS,
      visibleIf: { fieldId: `contact_${contactNum}_appointment_made`, value: 'No' },
      requiredIf: { fieldId: `contact_${contactNum}_appointment_made`, value: 'No' },
    }),
    createField({
      id: `contact_${contactNum}_cancellation_reason`,
      label: `Cancellation Reason`,
      type: FIELD_TYPES.SELECT,
      section,
      // "No answer" is only a valid cancellation reason on the 5th attempt
      options:
        contactNum >= 5
          ? ['No answer', ...SS_CONTACT_CANCELLATION_REASONS]
          : SS_CONTACT_CANCELLATION_REASONS,
      visibleIf: { fieldId: `contact_${contactNum}_appointment_made`, value: 'Cancel' },
      requiredIf: { fieldId: `contact_${contactNum}_appointment_made`, value: 'Cancel' },
      triggersLostCancelled: true,
    }),
  ];
};

const SCHEDULE_DELAY_REASONS = [
  'Gather funds',
  'Patient sick',
  'Work Commitment',
  'Personal Commitments',
  'Equipment Availability',
  'Other Health Priorities',
  'Patient Requested Date',
  'Courier 2 days',
];

// Reschedule attempts mirror the Contact to Schedule attempts.
// Attempt 1 keeps the original field ids so saved cases still load.
// The same shape is reused for the second reschedule (prefix `second_reschedule_`).
const rescheduleAttempt = (
  prefix,
  sectionPrefix,
  firstPreviousDate,
  delayReasons = SS_CONTACT_DELAY_REASONS,
  cancellationReasons = SS_CONTACT_CANCELLATION_REASONS
) => (contactNum) => {
  const section = `${sectionPrefix} Contact ${contactNum}`;
  const first = contactNum === 1;
  const id = (suffix) => (first ? `${prefix}_${suffix}` : `${prefix}_contact_${contactNum}_${suffix}`);
  const dateId = first ? `${prefix}_contact_date` : `${prefix}_contact_${contactNum}_date`;
  const previousDate =
    contactNum === 1
      ? firstPreviousDate
      : contactNum === 2
        ? `${prefix}_contact_date`
        : `${prefix}_contact_${contactNum - 1}_date`;
  return [
    createField({
      id: dateId,
      label: 'Date',
      type: FIELD_TYPES.DATE,
      section,
    }),
    createField({
      id: id('lead_time'),
      label: 'Lead Time (auto)',
      type: FIELD_TYPES.AUTO_CALC,
      section,
      autoCalcFrom: { startField: previousDate, endField: dateId, format: 'days' },
      helpText: first
        ? 'From the previously scheduled appointment date'
        : `From ${sectionPrefix.toLowerCase()} contact ${contactNum - 1}`,
    }),
    createField({
      id: id('appointment_made'),
      label: 'Appointment Made?',
      type: FIELD_TYPES.SELECT,
      section,
      options: APPOINTMENT_OUTCOMES,
    }),
    createField({
      id: id('delay_reason'),
      label: 'Delay Reason',
      type: FIELD_TYPES.SELECT,
      section,
      options: delayReasons,
      visibleIf: { fieldId: id('appointment_made'), value: 'No' },
      requiredIf: { fieldId: id('appointment_made'), value: 'No' },
    }),
    createField({
      id: id('cancellation_reason'),
      label: 'Cancellation Reason',
      type: FIELD_TYPES.SELECT,
      section,
      options: contactNum >= 5 ? ['No answer', ...cancellationReasons] : cancellationReasons,
      visibleIf: { fieldId: id('appointment_made'), value: 'Cancel' },
      requiredIf: { fieldId: id('appointment_made'), value: 'Cancel' },
      triggersLostCancelled: true,
    }),
  ];
};

const ssRescheduleAttempt = rescheduleAttempt('reschedule', 'Reschedule', 'scheduled_sleep_study_date');
const ssSecondRescheduleAttempt = rescheduleAttempt(
  'second_reschedule',
  'Second Reschedule',
  'rescheduled_sleep_study_date'
);


export const SS_SCHEDULE_CONFIG = createStageConfig('SS Schedule', [
  ...[1, 2, 3, 4, 5, 6].flatMap(ssContactAttempt),

  // Sleep Study Schedule — opens once an appointment was made
  createField({
    id: 'scheduler',
    label: 'Scheduler',
    type: FIELD_TYPES.SELECT,
    section: 'Sleep Study Schedule',
    options: SCHEDULERS,
  }),
  createField({
    id: 'today_date',
    label: "Today's Date",
    type: FIELD_TYPES.DATE,
    section: 'Sleep Study Schedule',
  }),
  createField({
    id: 'scheduled_sleep_study_date',
    label: 'Scheduled Sleep Study Date',
    type: FIELD_TYPES.DATE,
    section: 'Sleep Study Schedule',
    required: true,
  }),
  createField({
    id: 'scheduled_lead_time',
    label: 'Lead Time (auto)',
    type: FIELD_TYPES.AUTO_CALC,
    section: 'Sleep Study Schedule',
    autoCalcFrom: {
      startField: 'today_date',
      endField: 'scheduled_sleep_study_date',
      format: 'days',
    },
  }),
  createField({
    id: 'schedule_delay_reason',
    label: 'Delay Reason',
    type: FIELD_TYPES.SELECT,
    section: 'Sleep Study Schedule',
    options: SCHEDULE_DELAY_REASONS,
    visibleIf: { fieldId: 'scheduled_lead_time', greaterThan: 4 },
    requiredIf: { fieldId: 'scheduled_lead_time', greaterThan: 4 },
    helpText: 'Only asked when the lead time is more than 4 working days',
  }),
  createField({
    id: 'place_of_facilitation',
    label: 'Place of Facilitation',
    type: FIELD_TYPES.SELECT,
    section: 'Sleep Study Schedule',
    options: PLACES,
    required: true,
  }),

  // Reschedule — same progressive disclosure as the contacts above
  ...[1, 2, 3, 4, 5, 6].flatMap(ssRescheduleAttempt),

  // Second Sleep Study Schedule, from the reschedule contacts
  createField({
    id: 'reschedule_sleep_study_date',
    label: "Today's Date",
    type: FIELD_TYPES.DATE,
    section: 'Sleep Study Schedule (Reschedule)',
  }),
  createField({
    id: 'rescheduled_sleep_study_date',
    label: 'Scheduled Sleep Study Date',
    type: FIELD_TYPES.DATE,
    section: 'Sleep Study Schedule (Reschedule)',
    required: true,
  }),
  createField({
    id: 'reschedule_sleep_study_lead_time',
    label: 'Lead Time (auto)',
    type: FIELD_TYPES.AUTO_CALC,
    section: 'Sleep Study Schedule (Reschedule)',
    autoCalcFrom: {
      startField: 'reschedule_sleep_study_date',
      endField: 'rescheduled_sleep_study_date',
      format: 'days',
    },
  }),
  createField({
    id: 'reschedule_sleep_study_delay_reason',
    label: 'Delay Reason',
    type: FIELD_TYPES.SELECT,
    section: 'Sleep Study Schedule (Reschedule)',
    options: SCHEDULE_DELAY_REASONS,
    visibleIf: { fieldId: 'reschedule_sleep_study_lead_time', greaterThan: 4 },
    requiredIf: { fieldId: 'reschedule_sleep_study_lead_time', greaterThan: 4 },
    helpText: 'Only asked when the lead time is more than 4 working days',
  }),
  createField({
    id: 'reschedule_sleep_study_facilitation',
    label: 'Place of Facilitation',
    type: FIELD_TYPES.SELECT,
    section: 'Sleep Study Schedule (Reschedule)',
    options: PLACES,
    required: true,
  }),

  // Second Reschedule — only opens on a second visit to SS Schedule, once the first
  // reschedule already holds data
  ...[1, 2, 3, 4, 5, 6].flatMap(ssSecondRescheduleAttempt),

  createField({
    id: 'second_reschedule_sleep_study_date',
    label: "Today's Date",
    type: FIELD_TYPES.DATE,
    section: 'Sleep Study Schedule (Second Reschedule)',
  }),
  createField({
    id: 'second_rescheduled_sleep_study_date',
    label: 'Scheduled Sleep Study Date',
    type: FIELD_TYPES.DATE,
    section: 'Sleep Study Schedule (Second Reschedule)',
    required: true,
  }),
  createField({
    id: 'second_reschedule_sleep_study_lead_time',
    label: 'Lead Time (auto)',
    type: FIELD_TYPES.AUTO_CALC,
    section: 'Sleep Study Schedule (Second Reschedule)',
    autoCalcFrom: {
      startField: 'second_reschedule_sleep_study_date',
      endField: 'second_rescheduled_sleep_study_date',
      format: 'days',
    },
  }),
  createField({
    id: 'second_reschedule_sleep_study_delay_reason',
    label: 'Delay Reason',
    type: FIELD_TYPES.SELECT,
    section: 'Sleep Study Schedule (Second Reschedule)',
    options: SCHEDULE_DELAY_REASONS,
    visibleIf: { fieldId: 'second_reschedule_sleep_study_lead_time', greaterThan: 4 },
    requiredIf: { fieldId: 'second_reschedule_sleep_study_lead_time', greaterThan: 4 },
    helpText: 'Only asked when the lead time is more than 4 working days',
  }),
  createField({
    id: 'second_reschedule_sleep_study_facilitation',
    label: 'Place of Facilitation',
    type: FIELD_TYPES.SELECT,
    section: 'Sleep Study Schedule (Second Reschedule)',
    options: PLACES,
    required: true,
  }),
]);

// ============ SS STAGE 3: SS APPOINTMENT CONFIRMATION ============
export const SS_APPOINTMENT_CONFIRMATION_CONFIG = createStageConfig('SS Appointment Confirmation', [
  createField({
    id: 'confirmation_date',
    label: 'Date of Confirmation',
    type: FIELD_TYPES.DATE,
    section: 'Confirmation',
    required: true,
    autoFillToday: true,
    readOnlyAfterSave: true,
    helpText: 'Starts on today and locks once the stage is saved',
  }),
  createField({
    id: 'funding_method',
    label: 'Funding Method',
    type: FIELD_TYPES.SELECT,
    section: 'Funding',
    options: ['Medical aid', 'Private'],
    required: true,
    resetOnChange: ['medical_aid_authorization', 'payment_method_if_yes'],
  }),
  createField({
    id: 'medical_aid_name',
    label: 'Medical Aid Name',
    type: FIELD_TYPES.SELECT,
    section: 'Funding',
    options: MEDICAL_AID_OPTIONS,
    // The medical aid is already on the referral, so this stage never asks for it again.
    // Kept in the config so values captured before are still stored with the case.
    hidden: true,
  }),
  createField({
    id: 'medical_aid_authorization',
    label: 'Authorization Type',
    type: FIELD_TYPES.SELECT,
    section: 'Funding',
    options: ['Approved', 'Pending', 'Declined', 'Medical Aid Declined'],
    visibleIf: { fieldId: 'funding_method', value: 'Medical aid' },
    requiredIf: { fieldId: 'funding_method', value: 'Medical aid' },
  }),
  createField({
    id: 'appointment_confirmed',
    label: 'Appointment Confirmed?',
    type: FIELD_TYPES.SELECT,
    section: 'Outcome',
    options: ['Yes', 'No', 'Cancel'],
    required: true,
  }),
  createField({
    id: 'payment_method_if_yes',
    label: 'Payment Method',
    type: FIELD_TYPES.SELECT,
    section: 'Outcome',
    options: MEDICAL_AID_PAYMENT_METHODS,
    // Private funding can only ever be paid privately
    dynamicOptions: {
      fieldId: 'funding_method',
      byValue: { 'Medical aid': MEDICAL_AID_PAYMENT_METHODS, Private: ['Private'] },
    },
    visibleIf: { fieldId: 'appointment_confirmed', value: 'Yes' },
    requiredIf: { fieldId: 'appointment_confirmed', value: 'Yes' },
  }),
  createField({
    id: 'delay_reason',
    label: 'Delay Reason',
    type: FIELD_TYPES.SELECT,
    section: 'Outcome',
    options: [
      'Patient postponed',
      'Gathering funds',
      'No answer',
      'Other health issues',
      'Patient ill',
      'Work responsibility',
      'Medical aid waiting period',
      'Chronic registration needed',
      'Medical aid declined',
    ],
    visibleIf: { fieldId: 'appointment_confirmed', value: 'No' },
    requiredIf: { fieldId: 'appointment_confirmed', value: 'No' },
    // After saving, the rep is asked whether the case goes back for rescheduling
    offersSendBackTo: 'SS Schedule',
    helpText: 'Mandatory when the appointment was not confirmed — you are asked after saving whether to reschedule',
  }),
  createField({
    id: 'cancellation_reason',
    label: 'Cancellation Reason',
    type: FIELD_TYPES.SELECT,
    section: 'Outcome',
    options: [
      'Not interested',
      'Finances',
      'Level one',
      'Other health issues',
      'Pulmonology referral',
      'Medical aid declined',
    ],
    visibleIf: { fieldId: 'appointment_confirmed', value: 'Cancel' },
    requiredIf: { fieldId: 'appointment_confirmed', value: 'Cancel' },
    triggersLostCancelled: true,
    confirmAfterSave: true,
    helpText: 'Mandatory when Cancel is selected — you are asked to move the case after saving',
  }),
]);

// ============ SS STAGE 4: SS FACILITATION ============
export const SS_FACILITATION_CONFIG = createStageConfig('SS Facilitation', [
  createField({
    id: 'did_study_take_place',
    label: 'Did Study Take Place?',
    type: FIELD_TYPES.SELECT,
    section: 'Study Completion',
    options: APPOINTMENT_OUTCOMES,
    required: true,
  }),
  createField({
    id: 'facilitation_date',
    label: 'Date of Facilitation',
    type: FIELD_TYPES.DATE,
    section: 'Facilitation',
    autoFillToday: true,
    visibleIf: { fieldId: 'did_study_take_place', value: 'Yes' },
    requiredIf: { fieldId: 'did_study_take_place', value: 'Yes' },
  }),
  createField({
    id: 'who_facilitated',
    label: 'Who Facilitated PSG',
    type: FIELD_TYPES.SELECT,
    section: 'Facilitation',
    options: FACILITATORS_SS,
    visibleIf: { fieldId: 'did_study_take_place', value: 'Yes' },
    requiredIf: { fieldId: 'did_study_take_place', value: 'Yes' },
  }),
  createField({
    id: 'place_of_facilitation',
    label: 'Place of Facilitation',
    type: FIELD_TYPES.SELECT,
    section: 'Facilitation',
    options: PLACES,
    visibleIf: { fieldId: 'did_study_take_place', value: 'Yes' },
  }),
  createField({
    id: 'facilitation_problems',
    label: 'Facilitation Problems',
    type: FIELD_TYPES.SELECT,
    section: 'Facilitation',
    options: [
      'PSG Equipment not connecting to phone',
      'Equipment not charging',
      'Equipment set incomplete',
      'Connectivity',
      'Patient Struggle',
    ],
    visibleIf: { fieldId: 'did_study_take_place', value: 'Yes' },
  }),
  createField({
    id: 'study_took_place_on_time',
    label: 'Did the Study Take Place on Time?',
    type: FIELD_TYPES.SELECT,
    section: 'Facilitation',
    options: YES_NO,
    visibleIf: { fieldId: 'did_study_take_place', value: 'Yes' },
    helpText: 'A late study still counts — the case moves on either way',
  }),
  createField({
    id: 'delay_reason_if_no',
    label: 'Delay Reason',
    type: FIELD_TYPES.SELECT,
    section: 'Outcome',
    options: [
      'Did not show up',
      'Finances',
      'Patient sick',
      'Work responsibilities',
      'Patient postponed',
      'Other health issues',
    ],
    visibleIf: { fieldId: 'did_study_take_place', value: 'No' },
    requiredIf: { fieldId: 'did_study_take_place', value: 'No' },
    offersSendBackTo: 'SS Schedule',
    helpText: 'Mandatory when the study did not take place — you are asked after saving whether to reschedule',
  }),
  createField({
    id: 'cancellation_reason',
    label: 'Cancellation Reason',
    type: FIELD_TYPES.SELECT,
    section: 'Outcome',
    options: ['Finances', 'Not interested', 'Level one', 'Other health issues'],
    visibleIf: { fieldId: 'did_study_take_place', value: 'Cancel' },
    requiredIf: { fieldId: 'did_study_take_place', value: 'Cancel' },
    triggersLostCancelled: true,
    confirmAfterSave: true,
    helpText: 'Mandatory when Cancel is selected — you are asked to move the case after saving',
  }),
]);

// ============ SS STAGE 5: SS FOLLOW UP ============
export const SS_FOLLOWUP_CONFIG = withDataField(
  createStageConfig('SS Follow up', [
    createField({
      id: 'responsible_1st',
      label: 'Responsible',
      type: FIELD_TYPES.SELECT,
      section: 'First Contact',
      options: RESPONSIBLE_PEOPLE,
    }),
    createField({
      id: 'date_1st',
      label: 'Date',
      type: FIELD_TYPES.DATE,
      section: 'First Contact',
    }),
    createField({
      id: 'pts_ahi',
      label: "Pt's AHI",
      type: FIELD_TYPES.NUMBER,
      section: 'First Contact',
      min: 0,
      max: 999,
      helpText: '3 digits max',
    }),
    createField({
      id: 'is_redo_required',
      label: 'Is a Redo Required?',
      type: FIELD_TYPES.SELECT,
      section: 'First Contact',
      options: YES_NO,
    }),

    createField({
      id: 'equipment_serial_no',
      label: 'Equipment Serial Number',
      type: FIELD_TYPES.TEXT,
      section: 'Redo Reason',
      visibleIf: { fieldId: 'is_redo_required', value: 'Yes' },
      requiredIf: { fieldId: 'is_redo_required', value: 'Yes' },
    }),
    createField({
      id: 'redo_reason_type',
      label: 'Redo Reason Type',
      type: FIELD_TYPES.SELECT,
      section: 'Redo Reason',
      options: ['Equipment', 'Patient'],
      visibleIf: { fieldId: 'is_redo_required', value: 'Yes' },
      requiredIf: { fieldId: 'is_redo_required', value: 'Yes' },
    }),
    createField({
      id: 'redo_reason_equipment',
      label: 'Equipment Reason',
      type: FIELD_TYPES.SELECT,
      section: 'Redo Reason',
      options: [
        'I-Ring Disconnected',
        'Upload failed',
        'Insufficient hours',
        'SI System Update',
        'Ring Battery',
        'Office-Phone Battery',
        'Charger cable faulty',
        'Signal quality too low',
      ],
      visibleIf: { fieldId: 'redo_reason_type', value: 'Equipment' },
      requiredIf: { fieldId: 'redo_reason_type', value: 'Equipment' },
    }),
    createField({
      id: 'redo_reason_patient',
      label: 'Patient Reason',
      type: FIELD_TYPES.SELECT,
      section: 'Redo Reason',
      options: [
        'Low AHI',
        'High Central Apnea',
        'With Oral Device',
        'Signal quality too low',
        'Insufficient hours',
        'Patient request',
      ],
      visibleIf: { fieldId: 'redo_reason_type', value: 'Patient' },
      requiredIf: { fieldId: 'redo_reason_type', value: 'Patient' },
    }),

    createField({
      id: 'redo_successful_date',
      label: 'Date PSG Being Successful (Redo)',
      type: FIELD_TYPES.DATE,
      section: 'Redo SS',
      visibleIf: { fieldId: 'is_redo_required', value: 'Yes' },
    }),
    createField({
      id: 'qty_redos',
      label: 'Quantity of Redos',
      type: FIELD_TYPES.NUMBER,
      section: 'Redo SS',
      min: 1,
      max: 5,
      visibleIf: { fieldId: 'is_redo_required', value: 'Yes' },
    }),
  ]),
  'ss_followup_data'
);

// ============ SS STAGE 6: SS FEEDBACK ============
const ssFeedbackContact = (contactNum) => {
  const previous =
    contactNum === 1 ? 'facilitation_date' : `feedback_contact_${contactNum - 1}_date`;
  // Contact 1 measures from the facilitation date, which lives on the SS Facilitation stage
  const previousStage = contactNum === 1 ? 'ss_facilitation_data' : null;
  const section = `Feedback Contact ${contactNum}`;
  return [
    createField({
      id: `feedback_contact_${contactNum}_date`,
      label: 'Date',
      type: FIELD_TYPES.DATE,
      section,
    }),
    createField({
      id: `feedback_contact_${contactNum}_responsible`,
      label: 'Responsible',
      type: FIELD_TYPES.SELECT,
      section,
      options: RESPONSIBLE_PEOPLE,
    }),
    createField({
      id: `feedback_contact_${contactNum}_lead_time`,
      label: 'Lead Time (auto)',
      type: FIELD_TYPES.AUTO_CALC,
      section,
      autoCalcFrom: {
        startField: previous,
        startStage: previousStage,
        endField: `feedback_contact_${contactNum}_date`,
        format: 'days',
      },
      helpText:
        contactNum === 1 ? 'From facilitation date' : `From contact ${contactNum - 1} date`,
    }),
    createField({
      id: `feedback_contact_${contactNum}_delay_reason`,
      label: 'Delay Reason',
      type: FIELD_TYPES.SELECT,
      section,
      options: ['Forms Outstanding', 'Pt requested F/B date'],
      requiredIf: { fieldId: `feedback_contact_${contactNum}_lead_time`, greaterThan: 2 },
      helpText: 'Mandatory when the lead time is more than 2 days',
    }),
    createField({
      id: `feedback_contact_${contactNum}_feedback_provided`,
      label: 'Feedback Provided?',
      type: FIELD_TYPES.SELECT,
      section,
      options: YES_NO,
    }),
    createField({
      id: `feedback_contact_${contactNum}_no_reason`,
      label: 'Reason Not Provided',
      type: FIELD_TYPES.SELECT,
      section,
      options: FEEDBACK_NOT_PROVIDED_REASONS,
      visibleIf: { fieldId: `feedback_contact_${contactNum}_feedback_provided`, value: 'No' },
      requiredIf: { fieldId: `feedback_contact_${contactNum}_feedback_provided`, value: 'No' },
    }),
    createField({
      id: `feedback_contact_${contactNum}_details`,
      label: 'Feedback Details',
      type: FIELD_TYPES.TEXTAREA,
      section,
      placeholder: 'What the patient was told and how they responded...',
      visibleIf: { fieldId: `feedback_contact_${contactNum}_feedback_provided`, value: 'Yes' },
    }),
  ];
};

export const SS_FEEDBACK_CONFIG = createStageConfig(
  'SS Feedback',
  [1, 2, 3, 4, 5].flatMap(ssFeedbackContact)
);

// ============ SS STAGE 7: SS REPORTS ============
export const SS_REPORTS_CONFIG = createStageConfig('SS Reports', [
  createField({
    id: 'responsible_for_summary',
    label: 'Responsible for Summary',
    type: FIELD_TYPES.SELECT,
    section: 'Summary',
    options: ['MNO', 'DCO'],
  }),
  createField({
    id: 'date_psg_summarised',
    label: 'Date PSG Summarised',
    type: FIELD_TYPES.DATE,
    section: 'Summary',
  }),
  createField({
    id: 'lead_time_feedback_to_summarised',
    label: 'Lead Time: Feedback to Summarised (auto)',
    type: FIELD_TYPES.AUTO_CALC,
    section: 'Summary',
    autoCalcFrom: {
      startFrom: 'last_ss_feedback_yes',
      endField: 'date_psg_summarised',
      format: 'days',
    },
    helpText: 'From the last SS Feedback marked Yes, or from SS Follow up',
  }),
  createField({
    id: 'date_dr_scored',
    label: 'Date Doctor Scored',
    type: FIELD_TYPES.DATE,
    section: 'Scoring',
  }),
  createField({
    id: 'responsible_for_scoring',
    label: 'Responsible for Scoring',
    type: FIELD_TYPES.SELECT,
    section: 'Scoring',
    options: ['GRT', 'Darren', 'ILSE'],
  }),
  createField({
    id: 'lead_time_summarised_to_scoring',
    label: 'Lead Time: Summarised to Scoring (auto)',
    type: FIELD_TYPES.AUTO_CALC,
    section: 'Scoring',
    autoCalcFrom: { startField: 'date_psg_summarised', endField: 'date_dr_scored', format: 'days' },
    helpText: 'From PSG summarised to Dr scoring',
  }),
  createField({
    id: 'date_report_compiled',
    label: 'Date Report Compiled & Sent',
    type: FIELD_TYPES.DATE,
    section: 'Report Compiled & Sent',
  }),
  createField({
    id: 'responsible_for_report',
    label: 'Responsible for Report',
    type: FIELD_TYPES.SELECT,
    section: 'Report Compiled & Sent',
    options: ['ANN', 'FRA', 'KIR', 'MNO'],
  }),
  createField({
    id: 'lead_time_scoring_to_report',
    label: 'Lead Time: Scoring to Report (auto)',
    type: FIELD_TYPES.AUTO_CALC,
    section: 'Report Compiled & Sent',
    autoCalcFrom: { startField: 'date_dr_scored', endField: 'date_report_compiled', format: 'days' },
    helpText: 'From Dr scoring to report sent',
  }),
]);

// ============ TT STAGE 1: TT SCHEDULE ============
const TT_CONTACT_DELAY_REASONS = [
  'No answer',
  'Gather Funds',
  'Consult doctor',
  'Waiting for report',
  'Patient Sick',
  'Phone back later',
  'Work responsibilities',
  'Personal commitments',
  'Other Health Responsibilities',
];

const TT_CONTACT_CANCELLATION_REASONS = [
  'Funds',
  'Not Interested',
  'Already did study',
  '7hours',
  'Oral Device',
  'ENT',
  'Lifestyle changes',
  'Other Health Priorities',
  'AHI Non-Significant',
];

const ttContactAttempt = (contactNum) => {
  const previous = contactNum === 1 ? null : `tt_contact_${contactNum - 1}_date`;
  const section = `Contact ${contactNum}`;
  const cancellationReasons = [
    'Funds',
    'Not Interested',
    'Already did study',
    '7hours',
    'Oral Device',
    'ENT',
    'Lifestyle changes',
    'Other Health Priorities',
    'AHI Non-Significant',
  ];
  return [
    createField({
      id: `tt_contact_${contactNum}_date`,
      label: `Contact ${contactNum} Date`,
      type: FIELD_TYPES.DATE,
      section,
    }),
    createField({
      id: `tt_contact_${contactNum}_lead_time`,
      label: `Contact ${contactNum} Lead Time (auto)`,
      type: FIELD_TYPES.AUTO_CALC,
      section,
      autoCalcFrom: {
        startField: previous,
        startFrom: contactNum === 1 ? 'last_ss_feedback_yes' : null,
        endField: `tt_contact_${contactNum}_date`,
        format: 'days',
      },
      helpText:
        contactNum === 1
          ? 'From the last SS Feedback marked Yes'
          : `From contact ${contactNum - 1} date`,
    }),
    createField({
      id: `tt_contact_${contactNum}_appointment_made`,
      label: 'Appointment Made?',
      type: FIELD_TYPES.SELECT,
      section,
      options: APPOINTMENT_OUTCOMES,
    }),
    createField({
      id: `tt_contact_${contactNum}_no_appointment_reason`,
      label: 'Delay Reason',
      type: FIELD_TYPES.SELECT,
      section,
      options: [
        'No answer',
        'Gather Funds',
        'Consult doctor',
        'Waiting for report',
        'Patient Sick',
        'Phone back later',
        'Work responsibilities',
        'Personal commitments',
        'Other Health Responsibilities',
      ],
      visibleIf: { fieldId: `tt_contact_${contactNum}_appointment_made`, value: 'No' },
      requiredIf: { fieldId: `tt_contact_${contactNum}_appointment_made`, value: 'No' },
    }),
    createField({
      id: `tt_contact_${contactNum}_cancellation_reason`,
      label: 'Cancellation Reason',
      type: FIELD_TYPES.SELECT,
      section,
      options: contactNum === 5 ? ['No answer', ...cancellationReasons] : cancellationReasons,
      visibleIf: { fieldId: `tt_contact_${contactNum}_appointment_made`, value: 'Cancel' },
      requiredIf: { fieldId: `tt_contact_${contactNum}_appointment_made`, value: 'Cancel' },
      triggersLostCancelled: true,
    }),
  ];
};

const ttRescheduleAttempt = rescheduleAttempt(
  'tt_reschedule',
  'Reschedule',
  'scheduled_tt_date',
  TT_CONTACT_DELAY_REASONS,
  TT_CONTACT_CANCELLATION_REASONS
);
const ttSecondRescheduleAttempt = rescheduleAttempt(
  'tt_second_reschedule',
  'Second Reschedule',
  'rescheduled_tt_date',
  TT_CONTACT_DELAY_REASONS,
  TT_CONTACT_CANCELLATION_REASONS
);

const TT_SCHEDULE_DELAY_REASONS = [
  'Gather funds',
  'Patient sick',
  'Work Commitment',
  'Personal Commitment',
  'Equipment Availability',
  'Courier over 2 days',
  'Need report',
  'Pt requested Date',
  'CAI Equipment',
  'Other Health Priorities',
];

// The booking block of a titration attempt — reused for the reschedule and second reschedule
const ttBookingBlock = (section, prefix, scheduledDateId) => [
  createField({
    id: `${prefix}scheduler`,
    label: 'Scheduler',
    type: FIELD_TYPES.SELECT,
    section,
    options: SCHEDULERS,
  }),
  createField({
    id: `${prefix}today_date`,
    label: "Today's Date",
    type: FIELD_TYPES.DATE,
    section,
    autoFillToday: true,
    helpText: 'Date the appointment was booked',
  }),
  createField({
    id: scheduledDateId,
    label: 'Scheduled Titration Date',
    type: FIELD_TYPES.DATE,
    section,
    required: true,
  }),
  createField({
    id: `${prefix}scheduled_lead_time`,
    label: 'Lead Time (auto)',
    type: FIELD_TYPES.AUTO_CALC,
    section,
    autoCalcFrom: { startField: `${prefix}today_date`, endField: scheduledDateId, format: 'days' },
  }),
  createField({
    id: `${prefix}schedule_delay_reason`,
    label: 'Delay Reason',
    type: FIELD_TYPES.SELECT,
    section,
    options: TT_SCHEDULE_DELAY_REASONS,
    visibleIf: { fieldId: `${prefix}scheduled_lead_time`, greaterThan: 5 },
    requiredIf: { fieldId: `${prefix}scheduled_lead_time`, greaterThan: 5 },
    helpText: 'Mandatory when the lead time is more than 5 days',
  }),
  createField({
    id: `${prefix}place_of_facilitation`,
    label: 'Place of Facilitation',
    type: FIELD_TYPES.SELECT,
    section,
    options: PLACES,
  }),
];

export const TT_SCHEDULE_CONFIG = createStageConfig('TT Schedule', [
  ...[1, 2, 3, 4, 5, 6].flatMap(ttContactAttempt),

  ...ttBookingBlock('TT Schedule', 'tt_', 'scheduled_tt_date'),

  // Reschedule — opens once the first titration booking is saved
  ...[1, 2, 3, 4, 5, 6].flatMap(ttRescheduleAttempt),
  ...ttBookingBlock('TT Schedule (Reschedule)', 'tt_reschedule_', 'rescheduled_tt_date'),

  // Second Reschedule — only on a second visit to TT Schedule, once the first reschedule has data
  ...[1, 2, 3, 4, 5, 6].flatMap(ttSecondRescheduleAttempt),
  ...ttBookingBlock(
    'TT Schedule (Second Reschedule)',
    'tt_second_reschedule_',
    'second_rescheduled_tt_date'
  ),
]);

// ============ TT STAGE 2: TT APPOINTMENT CONFIRMATION ============
export const TT_APPOINTMENT_CONFIRMATION_CONFIG = createStageConfig('TT Appointment Confirmation', [
  createField({
    id: 'tt_confirmation_date',
    label: 'Date of Confirmation',
    type: FIELD_TYPES.DATE,
    section: 'Confirmation',
    required: true,
  }),
  createField({
    id: 'tt_funding_method',
    label: 'Funding Method',
    type: FIELD_TYPES.SELECT,
    section: 'Funding',
    options: ['Medical aid', 'Private'],
    required: true,
    resetOnChange: ['tt_medical_aid_authorization', 'tt_payment_method_if_yes'],
  }),
  createField({
    id: 'tt_medical_aid_authorization',
    label: 'Authorization Type',
    type: FIELD_TYPES.SELECT,
    section: 'Funding',
    options: ['Approved', 'Pending', 'Declined', 'Medical Aid Declined'],
    visibleIf: { fieldId: 'tt_funding_method', value: 'Medical aid' },
    requiredIf: { fieldId: 'tt_funding_method', value: 'Medical aid' },
  }),
  createField({
    id: 'tt_appointment_confirmed',
    label: 'Appointment Confirmed?',
    type: FIELD_TYPES.SELECT,
    section: 'Outcome',
    options: YES_NO,
    required: true,
  }),
  createField({
    id: 'tt_payment_method_if_yes',
    label: 'Payment Method',
    type: FIELD_TYPES.SELECT,
    section: 'Outcome',
    options: MEDICAL_AID_PAYMENT_METHODS,
    dynamicOptions: {
      fieldId: 'tt_funding_method',
      byValue: { 'Medical aid': MEDICAL_AID_PAYMENT_METHODS, Private: ['Private'] },
    },
    visibleIf: { fieldId: 'tt_appointment_confirmed', value: 'Yes' },
    requiredIf: { fieldId: 'tt_appointment_confirmed', value: 'Yes' },
  }),
  createField({
    id: 'tt_cancellation_reason_if_no',
    label: 'Cancellation Reason',
    type: FIELD_TYPES.SELECT,
    section: 'Outcome',
    options: [
      'Pt postponed',
      'Not interested',
      'Finances',
      'No answer',
      'Other health issues',
      'Pt ill',
      'Work responsibility',
    ],
    visibleIf: { fieldId: 'tt_appointment_confirmed', value: 'No' },
    requiredIf: { fieldId: 'tt_appointment_confirmed', value: 'No' },
    triggersLostCancelled: true,
  }),

  // Redo confirmations open one at a time: pre-redo -> 1st redo -> 2nd redo
  createField({
    id: 'tt_redo_1_attempted',
    label: 'First Redo Attempted?',
    type: FIELD_TYPES.SELECT,
    section: '1st Redo Confirmation',
    options: YES_NO,
    visibleIf: { fieldId: 'tt_appointment_confirmed', value: 'No' },
    helpText: 'Opens once the pre-redo appointment was not confirmed',
  }),
  createField({
    id: 'tt_redo_1_confirmation_date',
    label: 'First Redo Confirmation Date',
    type: FIELD_TYPES.DATE,
    section: '1st Redo Confirmation',
    visibleIf: { fieldId: 'tt_redo_1_attempted', value: 'Yes' },
    requiredIf: { fieldId: 'tt_redo_1_attempted', value: 'Yes' },
  }),
  createField({
    id: 'tt_redo_1_confirmed',
    label: 'First Redo Confirmed?',
    type: FIELD_TYPES.SELECT,
    section: '1st Redo Confirmation',
    options: YES_NO,
    visibleIf: { fieldId: 'tt_redo_1_attempted', value: 'Yes' },
    requiredIf: { fieldId: 'tt_redo_1_attempted', value: 'Yes' },
  }),
  createField({
    id: 'tt_redo_2_attempted',
    label: 'Second Redo Attempted?',
    type: FIELD_TYPES.SELECT,
    section: '2nd Redo Confirmation',
    options: YES_NO,
    visibleIf: { fieldId: 'tt_redo_1_confirmed', value: 'No' },
    helpText: 'Opens once the first redo was not confirmed',
  }),
  createField({
    id: 'tt_redo_2_confirmation_date',
    label: 'Second Redo Confirmation Date',
    type: FIELD_TYPES.DATE,
    section: '2nd Redo Confirmation',
    visibleIf: { fieldId: 'tt_redo_2_attempted', value: 'Yes' },
    requiredIf: { fieldId: 'tt_redo_2_attempted', value: 'Yes' },
  }),
  createField({
    id: 'tt_redo_2_confirmed',
    label: 'Second Redo Confirmed?',
    type: FIELD_TYPES.SELECT,
    section: '2nd Redo Confirmation',
    options: YES_NO,
    visibleIf: { fieldId: 'tt_redo_2_attempted', value: 'Yes' },
    requiredIf: { fieldId: 'tt_redo_2_attempted', value: 'Yes' },
  }),
]);


// ============ TT STAGE 3: TT FACILITATION (main + 2 redos) ============
const TT_FACILITATION_PROBLEMS = [
  'Mask straps loose',
  'Given masks not fitting',
  'Pt claustrophobic',
  'CPAP faulty',
  'CPAP noisy',
  'Patient negative',
  'Incomplete Equipment',
];

export const TT_FACILITATION_CONFIG = createStageConfig('TT Facilitation', [
  createField({
    id: 'did_titration_take_place',
    label: 'Did Titration Take Place?',
    type: FIELD_TYPES.SELECT,
    section: 'Titration Completion',
    options: APPOINTMENT_OUTCOMES,
    required: true,
  }),
  createField({
    id: 'tt_facilitation_date',
    label: 'Date of Titration',
    type: FIELD_TYPES.DATE,
    section: 'Titration',
    autoFillToday: true,
    visibleIf: { fieldId: 'did_titration_take_place', value: 'Yes' },
    requiredIf: { fieldId: 'did_titration_take_place', value: 'Yes' },
  }),
  createField({
    id: 'tt_who_facilitated',
    label: 'Who Facilitated Titration',
    type: FIELD_TYPES.SELECT,
    section: 'Titration',
    options: FACILITATORS_TT,
    visibleIf: { fieldId: 'did_titration_take_place', value: 'Yes' },
    requiredIf: { fieldId: 'did_titration_take_place', value: 'Yes' },
  }),
  createField({
    id: 'tt_place_of_facilitation',
    label: 'Place of Titration',
    type: FIELD_TYPES.SELECT,
    section: 'Titration',
    options: PLACES,
    visibleIf: { fieldId: 'did_titration_take_place', value: 'Yes' },
  }),
  createField({
    id: 'tt_facilitation_problems',
    label: 'Titration Problems',
    type: FIELD_TYPES.SELECT,
    section: 'Titration',
    options: TT_FACILITATION_PROBLEMS,
    visibleIf: { fieldId: 'did_titration_take_place', value: 'Yes' },
  }),
  createField({
    id: 'tt_study_took_place_on_time',
    label: 'Did Titration Take Place on Time?',
    type: FIELD_TYPES.SELECT,
    section: 'Titration',
    options: YES_NO,
    visibleIf: { fieldId: 'did_titration_take_place', value: 'Yes' },
    helpText: 'A late titration still counts — the case moves on either way',
  }),
  createField({
    id: 'tt_delay_reason_if_no',
    label: 'Delay Reason',
    type: FIELD_TYPES.SELECT,
    section: 'Outcome',
    options: [
      'Did not show up',
      'Finances',
      'Patient sick',
      'Work responsibilities',
      'Patient postponed',
      'Other health issues',
    ],
    visibleIf: { fieldId: 'did_titration_take_place', value: 'No' },
    requiredIf: { fieldId: 'did_titration_take_place', value: 'No' },
    offersSendBackTo: 'TT Schedule',
    helpText: 'Mandatory when the titration did not take place — you are asked after saving whether to reschedule',
  }),
  createField({
    id: 'tt_cancellation_reason',
    label: 'Cancellation Reason',
    type: FIELD_TYPES.SELECT,
    section: 'Outcome',
    options: ['Finances', 'Not interested', 'Other health issues'],
    visibleIf: { fieldId: 'did_titration_take_place', value: 'Cancel' },
    requiredIf: { fieldId: 'did_titration_take_place', value: 'Cancel' },
    triggersLostCancelled: true,
    confirmAfterSave: true,
    helpText: 'Mandatory when Cancel is selected — you are asked to move the case after saving',
  }),
]);

// ============ TT STAGE 4: TT NEXT DAY FOLLOW-UP ============
const ttNightFollowUp = (nightNum) => {
  const section = `Night ${nightNum} Follow-up`;
  return [
    createField({
      id: `tt_nextday_night_${nightNum}_contact_date`,
      label: `Night ${nightNum} Contact Date`,
      type: FIELD_TYPES.DATE,
      section,
    }),
    createField({
      id: `tt_nextday_night_${nightNum}_responsible`,
      label: 'Responsible',
      type: FIELD_TYPES.SELECT,
      section,
      options: ['FRA', 'DCO', 'KIR', 'MNO'],
    }),
    createField({
      id: `tt_nextday_night_${nightNum}_communication_type`,
      label: 'Communication Type',
      type: FIELD_TYPES.SELECT,
      section,
      options: ['WhatsApp', 'Phone call', 'Call and Whatsapp'],
    }),
    createField({
      id: `tt_nextday_night_${nightNum}_pt_feedback`,
      label: 'Pt Feedback (select all that apply)',
      type: FIELD_TYPES.MULTI_SELECT,
      section,
      options: [
        'No Answer',
        'Mask leakage',
        'Pt claustrophobic',
        'High AHI',
        'High pressures',
        'Dry mouth',
        'Condensation',
        'Mask straps loose',
        'Possible CPAP Faulty',
        'Exhale Difficulty',
        'Humidifier Adjustments',
        'Noise Complaint',
        'Smelly Equipment',
      ],
    }),
  ];
};

export const TT_NEXTDAY_FU_CONFIG = withDataField(
  createStageConfig('TT Next day FU', [
    ...[1, 2, 3, 4].flatMap(ttNightFollowUp),

    createField({
      id: 'tt_2nd_day_completion_date',
      label: 'Date of Completion',
      type: FIELD_TYPES.DATE,
      section: 'Date of Completion',
      required: true,
    }),
    createField({
      id: 'tt_nights_device_used',
      label: 'Nights Device Was Used',
      type: FIELD_TYPES.SELECT,
      section: 'Date of Completion',
      options: ['1', '2', '3', '4', '5', '6', '14'],
      required: true,
    }),
    createField({
      id: 'tt_2nd_day_delay_reason',
      label: 'Delay Reason',
      type: FIELD_TYPES.SELECT,
      section: 'Date of Completion',
      options: [
        'Pt struggled',
        'Pt claustrophobic',
        'GRT Requested',
        '2 week trial',
        'High AHI',
        'High Pressures',
        'Pressure Variation',
      ],
      helpText: 'Mandatory when completion took more than 4 days',
    }),
    createField({
      id: 'tt_reports_fb_uploaded_date',
      label: 'Date Reports/F/B Form Uploaded',
      type: FIELD_TYPES.DATE,
      section: 'Reports & Feedback Forms',
    }),
    createField({
      id: 'tt_reports_fb_lead_time',
      label: 'Lead Time: Completion to Upload (auto)',
      type: FIELD_TYPES.AUTO_CALC,
      section: 'Reports & Feedback Forms',
      autoCalcFrom: {
        startField: 'tt_2nd_day_completion_date',
        endField: 'tt_reports_fb_uploaded_date',
        format: 'days',
      },
      helpText: 'Should be less than 6 days',
    }),
    createField({
      id: 'tt_upload_delay_reason',
      label: 'Upload Delay Reason',
      type: FIELD_TYPES.SELECT,
      section: 'Reports & Feedback Forms',
      options: ['No answer', 'Patient did not complete and send forms'],
      requiredIf: { fieldId: 'tt_reports_fb_lead_time', greaterThan: 6 },
      helpText: 'Mandatory when the upload took more than 6 days',
    }),
    createField({
      id: 'tt_is_redo_required',
      label: 'Is Redo Required?',
      type: FIELD_TYPES.SELECT,
      section: 'Redo',
      options: YES_NO,
    }),
    createField({
      id: 'tt_redo_reason',
      label: 'Reason for Redo',
      type: FIELD_TYPES.SELECT,
      section: 'Redo',
      options: TT_REDO_REASONS,
      visibleIf: { fieldId: 'tt_is_redo_required', value: 'Yes' },
      requiredIf: { fieldId: 'tt_is_redo_required', value: 'Yes' },
    }),
  ]),
  'tt_nextday_fu_data'
);

// ============ TT STAGE 5: TT REPORT ============
const ttReportRedo = (redoNum) => {
  const section = `Redo ${redoNum}`;
  return [
    createField({
      id: `tt_redo_${redoNum}_date`,
      label: 'Date of Redo',
      type: FIELD_TYPES.DATE,
      section,
    }),
    createField({
      id: `tt_redo_${redoNum}_reason`,
      label: 'Reason',
      type: FIELD_TYPES.SELECT,
      section,
      options: TT_REDO_REASONS,
    }),
  ];
};

export const TT_REPORT_CONFIG = createStageConfig('TT Report', [
  createField({
    id: 'tt_data_summarised_date',
    label: 'Date TT Data Summarised',
    type: FIELD_TYPES.DATE,
    section: 'Reporting',
    required: true,
  }),
  createField({
    id: 'tt_lead_time_summarised_to_scoring',
    label: 'Lead Time: Summarised to Scoring (auto)',
    type: FIELD_TYPES.AUTO_CALC,
    section: 'Reporting',
    autoCalcFrom: {
      startField: 'tt_data_summarised_date',
      endField: 'tt_dr_scored_date',
      format: 'days',
    },
  }),
  createField({
    id: 'tt_dr_scored_date',
    label: 'Date Dr Scored',
    type: FIELD_TYPES.DATE,
    section: 'Reporting',
    required: true,
  }),
  createField({
    id: 'tt_lead_time_scoring_to_report',
    label: 'Lead Time: Scoring to Report (auto)',
    type: FIELD_TYPES.AUTO_CALC,
    section: 'Reporting',
    autoCalcFrom: {
      startField: 'tt_dr_scored_date',
      endField: 'tt_report_compiled_date',
      format: 'days',
    },
  }),
  createField({
    id: 'tt_report_compiled_date',
    label: 'Date TT Report Compiled & Sent',
    type: FIELD_TYPES.DATE,
    section: 'Reporting',
    required: true,
  }),
  ...[1, 2, 3].flatMap(ttReportRedo),
]);

// ============ TT STAGE 6: TT FEEDBACK ============
const ttFeedbackContact = (contactNum) => {
  const previous =
    contactNum === 1 ? 'tt_report_compiled_date' : `tt_feedback_contact_${contactNum - 1}_date`;
  // Contact 1 measures from the TT report being sent, which lives on the TT Report stage
  const previousStage = contactNum === 1 ? 'tt_report_data' : null;
  const section = `Feedback Contact ${contactNum}`;
  return [
    createField({
      id: `tt_feedback_contact_${contactNum}_date`,
      label: 'Date',
      type: FIELD_TYPES.DATE,
      section,
    }),
    createField({
      id: `tt_feedback_contact_${contactNum}_responsible`,
      label: 'Responsible',
      type: FIELD_TYPES.SELECT,
      section,
      options: RESPONSIBLE_PEOPLE,
    }),
    createField({
      id: `tt_feedback_contact_${contactNum}_lead_time`,
      label: 'Lead Time (auto)',
      type: FIELD_TYPES.AUTO_CALC,
      section,
      autoCalcFrom: {
        startField: previous,
        startStage: previousStage,
        endField: `tt_feedback_contact_${contactNum}_date`,
        format: 'days',
      },
      helpText:
        contactNum === 1
          ? 'From the date the TT report was sent'
          : `From contact ${contactNum - 1} date`,
    }),
    createField({
      id: `tt_feedback_contact_${contactNum}_delay_reason`,
      label: 'Delay Reason',
      type: FIELD_TYPES.SELECT,
      section,
      options: [
        'No answer',
        'Discuss with Dr',
        'Pt wants to think about it',
        'Gather funds',
        'Other Health Priorities',
        'Read Report First',
        'Personal Commitments',
        'Work Commitments',
        'Forms Outstanding',
        'Pt requested F/B date',
      ],
      requiredIf: { fieldId: `tt_feedback_contact_${contactNum}_lead_time`, greaterThan: 2 },
      helpText: 'Mandatory when the lead time is more than 2 days',
    }),
    createField({
      id: `tt_feedback_contact_${contactNum}_feedback_provided`,
      label: 'Feedback Provided?',
      type: FIELD_TYPES.SELECT,
      section,
      options: YES_NO,
    }),
    createField({
      id: `tt_feedback_contact_${contactNum}_no_reason`,
      label: 'Reason Not Provided',
      type: FIELD_TYPES.SELECT,
      section,
      options: FEEDBACK_NOT_PROVIDED_REASONS,
      visibleIf: { fieldId: `tt_feedback_contact_${contactNum}_feedback_provided`, value: 'No' },
      requiredIf: { fieldId: `tt_feedback_contact_${contactNum}_feedback_provided`, value: 'No' },
    }),
  ];
};

export const TT_FEEDBACK_CONFIG = createStageConfig('TT Feedback', [
  ...[1, 2, 3, 4].map((reportNum) =>
    createField({
      id: `tt_report_outcome_${reportNum}`,
      label: `Report ${reportNum} Outcome`,
      type: FIELD_TYPES.SELECT,
      section: 'Report Outcome',
      options: ['CPAP', 'Oral device', 'Redo'],
    })
  ),
  createField({
    id: 'tt_feedback_redo_reason',
    label: 'Reason for Redo',
    type: FIELD_TYPES.SELECT,
    section: 'Report Outcome',
    options: TT_REDO_REASONS,
    visibleIf: { fieldId: 'tt_report_outcome_1', value: 'Redo' },
  }),

  ...[1, 2, 3, 4].flatMap(ttFeedbackContact),

  createField({
    id: 'tt_patient_next_step',
    label: 'Patient Feedback / Next Step (select all that apply)',
    type: FIELD_TYPES.MULTI_SELECT,
    section: 'Patient Next Steps',
    options: [
      'CPAP',
      'ENT',
      'Oral device',
      'Discuss with doctor',
      'Read report',
      'Wants to think about it',
      'Lifestyle changes',
      'Other health priorities',
      'Already on cpap',
      'Not interested',
    ],
  }),
  createField({
    id: 'tt_referred_to',
    label: 'Referred To',
    type: FIELD_TYPES.SELECT,
    section: 'Patient Next Steps',
    options: ['Sleep Assist', '7Hours', 'Ecomed', 'Dental', 'Other', 'ENT'],
  }),
  createField({
    id: 'tt_referral_date',
    label: 'Referral Date',
    type: FIELD_TYPES.DATE,
    section: 'Patient Next Steps',
  }),
  createField({
    id: 'tt_referral_lead_time',
    label: 'Referral Lead Time (auto)',
    type: FIELD_TYPES.AUTO_CALC,
    section: 'Patient Next Steps',
    autoCalcFrom: {
      startField: 'tt_feedback_contact_4_date',
      endField: 'tt_referral_date',
      format: 'days',
    },
    helpText: 'From feedback given to referral date',
  }),
]);


export const STAGE_CONFIGS = {
  'Referral': REFERRAL_CONFIG,
  'Referrals': REFERRAL_CONFIG, // backend stage name
  'Pre SS': PRE_SS_CONFIG,
  'SS Authorization': SS_AUTHORIZATION_CONFIG,
  'SS Facilitation': SS_FACILITATION_CONFIG,
  'SS Success': SS_SUCCESS_CONFIG,
  'Post SS': POST_SS_CONFIG,
  'SS Reports': SS_REPORTS_CONFIG,
  'TT Facilitation': TT_FACILITATION_CONFIG,
  'Post TT': POST_TT_CONFIG,
  'TT Sum': TT_SUM_CONFIG,
  'TT Report': TT_REPORT_CONFIG,
  'Lost/Cancelled': LOST_CANCELLED_CONFIG,
};

export const getStageConfig = (stageName) => STAGE_CONFIGS[stageName] || null;


// ---------- Dual pipeline: Sleep Study (SS) and Titration (TT) ----------
export const SS_STAGES = [
  "Referral",
  "SS Schedule",
  "SS Appointment Confirmation",
  "SS Facilitation",
  "SS Follow up",
  "SS Reports",
  "Done",
  "Lost/Cancelled",
];

// SS Feedback now opens the Titration pipeline
export const TT_STAGES = [
  "SS Feedback",
  "TT Schedule",
  "TT Appointment Confirmation",
  "TT Facilitation",
  "TT Next day FU",
  "TT Report",
  "TT Feedback",
  "Done",
  "Lost/Cancelled",
];

export const SS_STAGE_COLORS = {
  "Referral": { bg: "bg-slate-100", border: "border-slate-300", text: "text-slate-800" },
  "SS Schedule": { bg: "bg-blue-100", border: "border-blue-300", text: "text-blue-800" },
  "SS Appointment Confirmation": { bg: "bg-cyan-100", border: "border-cyan-300", text: "text-cyan-800" },
  "SS Facilitation": { bg: "bg-amber-100", border: "border-amber-300", text: "text-amber-800" },
  "SS Follow up": { bg: "bg-green-100", border: "border-green-300", text: "text-green-800" },
  "SS Reports": { bg: "bg-pink-100", border: "border-pink-300", text: "text-pink-800" },
  "Done": { bg: "bg-emerald-100", border: "border-emerald-300", text: "text-emerald-800" },
  "Lost/Cancelled": { bg: "bg-red-100", border: "border-red-300", text: "text-red-800" },
};

export const TT_STAGE_COLORS = {
  "SS Feedback": { bg: "bg-purple-100", border: "border-purple-300", text: "text-purple-800" },
  "TT Schedule": { bg: "bg-indigo-100", border: "border-indigo-300", text: "text-indigo-800" },
  "TT Appointment Confirmation": { bg: "bg-violet-100", border: "border-violet-300", text: "text-violet-800" },
  "TT Facilitation": { bg: "bg-rose-100", border: "border-rose-300", text: "text-rose-800" },
  "TT Next day FU": { bg: "bg-orange-100", border: "border-orange-300", text: "text-orange-800" },
  "TT Report": { bg: "bg-lime-100", border: "border-lime-300", text: "text-lime-800" },
  "TT Feedback": { bg: "bg-teal-100", border: "border-teal-300", text: "text-teal-800" },
  "Done": { bg: "bg-emerald-100", border: "border-emerald-300", text: "text-emerald-800" },
  "Lost/Cancelled": { bg: "bg-red-100", border: "border-red-300", text: "text-red-800" },
};

// Legacy 10-stage names mapped onto the dual-pipeline columns (display only)
export const LEGACY_STAGE_MAP = {
  "Referrals": "Referral",
  "Pre SS": "SS Schedule",
  "SS Authorization": "SS Schedule",
  "Post SS": "SS Follow up",
  "SS Reports": "SS Reports",
  "TT Facilitation": "TT Facilitation",
  "Post TT": "TT Next day FU",
  "TT Sum": "TT Report",
  "TT Report": "TT Report",
};

// ============ END STATE: DONE ============
export const DONE_CONFIG = createStageConfig('Done', [
  createField({
    id: 'completion_date',
    label: 'Completion Date',
    type: FIELD_TYPES.DATE,
    section: 'Completion',
    required: true,
    autoFillToday: true,
    readOnlyAfterSave: true,
  }),
  createField({
    id: 'patient_outcome',
    label: 'Patient Outcome',
    type: FIELD_TYPES.SELECT,
    section: 'Completion',
    options: ['Patient Enrolled', 'Patient Declined', 'Patient Inactive', 'Other'],
    required: true,
  }),
  createField({
    id: 'completion_notes',
    label: 'Completion Notes',
    type: FIELD_TYPES.TEXTAREA,
    section: 'Completion',
  }),
]);


// Drag-to-stage routing map for the dual pipeline
export const STAGE_CONFIG_MAP = {
  // Shared
  'Referral': REFERRAL_CONFIG,
  'Referrals': REFERRAL_CONFIG,
  'Lost/Cancelled': LOST_CANCELLED_CONFIG,
  'Done': DONE_CONFIG,

  // SS pipeline
  'SS Schedule': SS_SCHEDULE_CONFIG,
  'SS Appointment Confirmation': SS_APPOINTMENT_CONFIRMATION_CONFIG,
  'SS Facilitation': SS_FACILITATION_CONFIG,
  'SS Follow up': SS_FOLLOWUP_CONFIG,
  'SS Feedback': SS_FEEDBACK_CONFIG,
  'SS Reports': SS_REPORTS_CONFIG,

  // TT pipeline
  'TT Schedule': TT_SCHEDULE_CONFIG,
  'TT Appointment Confirmation': TT_APPOINTMENT_CONFIRMATION_CONFIG,
  'TT Facilitation': TT_FACILITATION_CONFIG,
  'TT Next day FU': TT_NEXTDAY_FU_CONFIG,
  'TT Report': TT_REPORT_CONFIG,
  'TT Feedback': TT_FEEDBACK_CONFIG,
};

// Legacy stage names still on existing cases resolve to their new-stage form
Object.entries(LEGACY_STAGE_MAP).forEach(([legacy, mapped]) => {
  if (!STAGE_CONFIG_MAP[legacy] && STAGE_CONFIG_MAP[mapped]) {
    STAGE_CONFIG_MAP[legacy] = STAGE_CONFIG_MAP[mapped];
  }
});

Object.assign(STAGE_CONFIGS, STAGE_CONFIG_MAP);
